<script setup lang="ts">
import { useAuth } from "@features/authentication/composables";
import { useProducts } from "@features/shopping/composables";
import { USER_MENU } from "@shared/constants/navigations";
import { slugify } from "@shared/utils/slugify";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@ui/collapsible";
import { Sheet, SheetContent, SheetTrigger } from "@ui/sheet";
import { MenuIcon } from "lucide-vue-next";
import { ref } from "vue";
import { RouterLink } from "vue-router";

const { authenticated } = useAuth();
const { categories } = useProducts();

const open = ref<boolean>(false);

const close = () => {
  open.value = false;
};
</script>

<template>
  <Sheet v-model:open="open">
    <SheetTrigger as-child class="lg:hidden">
      <MenuIcon class="size-7 cursor-pointer" />
    </SheetTrigger>
    <SheetContent side="left" class="flex w-[260px] flex-col">
      <!-- Product Category -->
      <Collapsible>
        <CollapsibleTrigger as-child>
          <div class="flex w-full cursor-pointer items-center justify-between gap-1.5 text-content">
            <RouterLink to="/shop" @click="close">Shop</RouterLink>
            <img
              src="@assets/images/common/arrow.svg"
              width="16"
              height="16"
              alt="Dropdown arrow"
              class="icon-collapse transition-transform duration-300 ease-in-out"
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <nav class="flex flex-col gap-2 py-4 pl-2 text-content" aria-label="Product categories">
            <RouterLink
              v-for="category in categories"
              :key="category.product_cat_id"
              :to="`/product-category/${slugify(category.product_cat_name)}`"
              @click="close"
            >
              {{ category.product_cat_name }}
            </RouterLink>
          </nav>
        </CollapsibleContent>
      </Collapsible>

      <!-- My Account -->
      <RouterLink v-if="!authenticated" to="/my-account" class="text-content" @click="close">
        Login
      </RouterLink>
      <Collapsible v-else>
        <CollapsibleTrigger as-child>
          <div class="flex w-full cursor-pointer items-center justify-between gap-1.5 text-content">
            <RouterLink to="/my-account" @click="close"> My Account </RouterLink>
            <img
              src="@assets/images/common/arrow.svg"
              width="16"
              height="16"
              alt="Dropdown arrow"
              class="icon-collapse transition-transform duration-300 ease-in-out"
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <nav class="flex flex-col gap-2 py-4 pl-2 text-content" aria-label="User menu items">
            <RouterLink v-for="menu in USER_MENU" :key="menu.name" :to="menu.path" @click="close">
              {{ menu.name }}
            </RouterLink>
          </nav>
        </CollapsibleContent>
      </Collapsible>
    </SheetContent>
  </Sheet>
</template>

<style lang="css" scoped>
[data-state="open"] .icon-collapse {
  transform: rotate(180deg);
}

[data-state="closed"] .icon-collapse {
  transform: rotate(0deg);
}
</style>
